import { inject, Injectable } from '@angular/core';
import { dsDtos } from '@vectronic/openApi-customer';

import { combineLatest, map, Observable, of } from 'rxjs';
import { RemoteCommandProviderService } from '../remote-command-provider.service';

@Injectable({ providedIn: 'root' })
export class CamerasFormatService {
  private remoteCommandProviderService = inject(RemoteCommandProviderService, { optional: true });

  cameras$ = this.remoteCommandProviderService?.cameraResponse$ ?? of();
  deviceTypes$ = this.remoteCommandProviderService?.deviceTypes$ ?? of();

  private cameraFormatsByRule$(ruleType: 'VIDEO' | 'PICTURE'): Observable<dsDtos.CameraFormat[]> {
    return combineLatest([this.cameras$, this.deviceTypes$]).pipe(
      map(([cameras, deviceTypes]) => {
        const camerasArray = cameras ? Object.values(cameras) : [];
        return camerasArray
          ?.map(
            ({ deviceTypeId }) => deviceTypes.find(({ id }) => id == deviceTypeId)?.cameraFormats,
          )
          ?.flat()
          ?.filter((cameraFormat) => cameraFormat?.cameraScheduleRuleType === ruleType)
          ?.filter(
            (value, index, array) =>
              array.findIndex(({ formatId }) => formatId === value.formatId) === index,
          );
      }),
    );
  }

  private mapCameraFormatsToOptions = (cameraFormats: dsDtos.CameraFormat[]) =>
    cameraFormats.map((cameraFormat) => ({
      value: cameraFormat.formatId,
      label: cameraFormat.information,
    }));

  videoFormatsOptions$: Observable<
    {
      value: number;
      label: string;
    }[]
  > = this.cameraVideoFormats$.pipe(
    map((cameraFormats) => this.mapCameraFormatsToOptions(cameraFormats)),
  );

  photoFormatsOptions$: Observable<
    {
      value: number;
      label: string;
    }[]
  > = this.cameraPhotoFormats$.pipe(
    map((cameraFormats) => this.mapCameraFormatsToOptions(cameraFormats)),
  );

  get cameraVideoFormats$(): Observable<dsDtos.CameraFormat[]> {
    return this.cameraFormatsByRule$('VIDEO');
  }

  get cameraPhotoFormats$(): Observable<dsDtos.CameraFormat[]> {
    return this.cameraFormatsByRule$('PICTURE');
  }

  getCameraFormatsFromDeviceType$(
    ruleType: 'VIDEO' | 'PICTURE',
    deviceTypeName = 'Vertex Vision',
  ): Observable<{ value: number; label: string }[]> {
    return this.deviceTypes$.pipe(
      map((deviceTypes) => {
        const matchingDeviceType = (deviceTypes ?? []).find((dt) => {
          const name = (dt.customerName || dt.name || '').toLowerCase();
          return name.includes(deviceTypeName.toLowerCase());
        });
        const formats = (matchingDeviceType?.cameraFormats ?? []).filter(
          (f) => f.cameraScheduleRuleType === ruleType,
        );
        return formats.map((f) => ({ value: f.formatId, label: f.information }));
      }),
    );
  }

  get videoFormatsFromDeviceType$(): Observable<{ value: number; label: string }[]> {
    return this.getCameraFormatsFromDeviceType$('VIDEO');
  }

  get photoFormatsFromDeviceType$(): Observable<{ value: number; label: string }[]> {
    return this.getCameraFormatsFromDeviceType$('PICTURE');
  }
}
